<template>
  <!-- Auth gate -->
  <div v-if="!isAdmin" class="login-container">
    <el-card class="login-card">
      <h2>Admin access required</h2>
      <p style="text-align:center;color:#555;margin-bottom:1.5rem">
        You need to be logged in as an admin to view player statistics.
      </p>
      <router-link to="/manage-cards">
        <button type="button"><i class="fas fa-arrow-left"></i> Go to admin login</button>
      </router-link>
    </el-card>
  </div>

  <div v-else id="game-statistics-page" class="admin-page">
    <AdminTabs />

    <div class="admin-page-header">
      <h1>Player Statistics</h1>
      <StatsViewToggle v-if="selectedUserId && sessions.length" v-model="view" />
    </div>

    <div class="stats-page-toolbar">
      <div class="stats-user-picker">
        <label class="admin-label" for="stats-user">Player</label>
        <el-select
          id="stats-user"
          v-model="selectedUserId"
          class="stats-user-select"
          placeholder="All players"
          clearable
          filterable
          @change="loadSelectedUser"
        >
          <el-option
            v-for="user in users"
            :key="user.user_id"
            :label="userLabel(user)"
            :value="user.user_id"
          />
        </el-select>
      </div>
    </div>

    <!-- Overview across all players -->
    <div v-if="!selectedUserId" class="admin-table-wrap">
      <div class="admin-card-head">
        <h3>All players</h3>
        <span class="admin-card-count">{{ users.length }} users</span>
      </div>

      <div v-if="loadingUsers" class="admin-state">
        <i class="fas fa-circle-notch fa-spin" style="margin-right:0.5rem"></i>
        Loading players…
      </div>

      <div v-else class="admin-table-wrap--scroll">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Status</th>
              <th class="numeric">Games</th>
              <th class="numeric">Correct</th>
              <th class="numeric">Mistakes</th>
              <th class="numeric">Cards dealt</th>
              <th class="numeric">Best streak</th>
              <th>Last played</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in overviewRows" :key="user.user_id">
              <td class="col-email">{{ user.email }}</td>
              <td>
                <span
                  class="admin-badge"
                  :class="user.status === 'active' ? 'admin-badge--active' : 'admin-badge--disabled'"
                >
                  {{ user.status }}
                </span>
              </td>
              <td class="numeric">{{ user.games_played }}</td>
              <td class="numeric">
                <span v-if="user.games_played" class="accuracy" :class="user.accuracyBand">
                  {{ user.percentCorrect }}%
                </span>
                <span v-else>&mdash;</span>
              </td>
              <td class="numeric">{{ user.mistakes }}</td>
              <td class="numeric">{{ user.cards_dealt }}</td>
              <td class="numeric">{{ user.longest_streak || '—' }}</td>
              <td>{{ user.lastPlayed }}</td>
              <td class="col-actions">
                <button
                  v-if="user.games_played"
                  type="button"
                  class="admin-btn admin-btn--secondary"
                  @click="selectUser(user.user_id)"
                >
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Single player drill-down -->
    <template v-else>
      <div v-if="loadingDetail" class="admin-card">
        <div class="admin-state">
          <i class="fas fa-circle-notch fa-spin" style="margin-right:0.5rem"></i>
          Loading statistics…
        </div>
      </div>

      <div v-else-if="!sessions.length" class="admin-card">
        <div class="admin-state admin-state--rich">
          <i class="fas fa-chart-line"></i>
          <h3>No games recorded for this player</h3>
          <p>
            Statistics appear once the player finishes a game while signed in with
            their email address.
          </p>
        </div>
      </div>

      <template v-else>
        <StatsSummary
          v-if="view === 'stats'"
          :sessions="sessions"
          :departments="departments"
        />
        <StatsCharts v-else :sessions="sessions" />
      </template>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import AdminTabs from '@/components/AdminTabs.vue';
import StatsSummary from '@/components/stats/StatsSummary.vue';
import StatsCharts from '@/components/stats/StatsCharts.vue';
import StatsViewToggle from '@/components/stats/StatsViewToggle.vue';
import { supabase } from '@/supabase';
import { fetchAllUserStats, fetchSessionsForUser } from '@/services/statsService';

const isAdmin = ref(false);
const view = ref('stats');
const users = ref([]);
const departments = ref([]);
const selectedUserId = ref(null);
const sessions = ref([]);
const loadingUsers = ref(true);
const loadingDetail = ref(false);

onMounted(async () => {
  if (localStorage.getItem('loggedIn') === 'true' && localStorage.getItem('adminUser')) {
    isAdmin.value = true;
    await Promise.all([loadUsers(), loadDepartments()]);
  } else {
    loadingUsers.value = false;
  }
});

const loadUsers = async () => {
  loadingUsers.value = true;
  try {
    users.value = await fetchAllUserStats();
  } finally {
    loadingUsers.value = false;
  }
};

const loadDepartments = async () => {
  const { data } = await supabase.from('departments').select('id, name').order('position');
  departments.value = data || [];
};

const loadSelectedUser = async () => {
  if (!selectedUserId.value) {
    sessions.value = [];
    return;
  }

  loadingDetail.value = true;
  try {
    sessions.value = await fetchSessionsForUser(selectedUserId.value);
  } finally {
    loadingDetail.value = false;
  }
};

const selectUser = (userId) => {
  selectedUserId.value = userId;
  loadSelectedUser();
};

const userLabel = (user) =>
  user.games_played ? `${user.email} (${user.games_played} games)` : `${user.email} (no games)`;

const overviewRows = computed(() =>
  users.value.map((user) => {
    const percentCorrect = user.cards_total
      ? Math.round((user.cards_correct / user.cards_total) * 1000) / 10
      : 0;

    return {
      ...user,
      percentCorrect,
      accuracyBand: percentCorrect >= 80 ? 'is-high' : percentCorrect >= 50 ? 'is-mid' : 'is-low',
      lastPlayed: user.last_played_at
        ? new Date(user.last_played_at).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : '—',
    };
  })
);
</script>
